import { Button, Flex, Result } from "antd";
import { Suspense, useEffect, useState } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";

import { Footer } from "@/components/Footer";
import { ROUTES } from "@/constants";
import { UpdateBanner } from "@/features/UpdateBanner";
import { useAppSelector } from "@/hooks/redux";
import { logoutService } from "@/Services/Logout";
import { RootState } from "@/store";

import routes from "..";
import NotFound from "./NotFound";

interface PrivateWrapperProps {
    children: JSX.Element;
    allowedRoles?: string[];
}

const PrivateWrapper = ({ children, allowedRoles }: PrivateWrapperProps) => {
    const navigate = useNavigate();
    const { isAuthenticated } = useAppSelector((state) => state.auth);
    const { role } = useAppSelector((state) => state.user);
    const location = useLocation();
    const wrappingLength = location.pathname.split("/");

    if (!isAuthenticated) {
        navigate(ROUTES.Login);
        return;
    }

    if (allowedRoles && !allowedRoles.includes(role)) {
        return <Unauthorized />;
    }

    const renderFooter = () => {
        if (wrappingLength?.[1] === "admin") {
            return;
        }

        if (wrappingLength?.length < 4) {
            return <Footer />;
        }

        return null;
    };

    return (
        <>
            <UpdateBanner />
            {children}
            {renderFooter()}
        </>
    );
};

const ResultWrapper = styled.div`
    .ant-result-title,
    .ant-result-subtitle {
        color: black;
    }
`;

const Unauthorized = () => {
    const navigate = useNavigate();
    const { isAuthenticated } = useAppSelector((s) => s.auth);

    return (
        <ResultWrapper>
            <Result
                status="403"
                title="403"
                subTitle="Вибачте, у вас немає доступу до цієї сторінки."
                extra={
                    <Flex vertical gap={8}>
                        <Button onClick={() => navigate("/")} type="primary">
                            Повернутися на головну
                        </Button>

                        {isAuthenticated && (
                            <Button onClick={() => logoutService()}>
                                Вийти з облікового запису
                            </Button>
                        )}
                    </Flex>
                }
            />
        </ResultWrapper>
    );
};

const Pages = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const versionChanged = useAppSelector(
        (state: RootState) => state._versionChanged, // err
    );
    const [hasRedirected, setHasRedirected] = useState(false);

    useEffect(() => {
        if (versionChanged && !hasRedirected) {
            setHasRedirected(true);
            navigate("/work-plan");
        }
    }, [versionChanged, hasRedirected, navigate, location]);

    return (
        <Suspense fallback={<div>Завантаження...</div>}>
            <Routes>
                {Object.values(routes).map(
                    ({
                        path,
                        component: Component,
                        isPrivate,
                        allowedRoles,
                    }) => (
                        <Route
                            key={path}
                            path={path}
                            element={
                                isPrivate ? (
                                    <PrivateWrapper allowedRoles={allowedRoles}>
                                        <Component />
                                    </PrivateWrapper>
                                ) : (
                                    <Component />
                                )
                            }
                        />
                    ),
                )}
                <Route path="*" element={<NotFound />} />
            </Routes>
        </Suspense>
    );
};

export default Pages;
